import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { wordpressWidgetAPI, propertiesAPI, widgetAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, Code, Copy, CheckCircle2, Trash2, Plus, 
  Eye, Calendar, Loader2, Info, ExternalLink,
  Palette, LayoutGrid, Search, Building2, BarChart3, 
  TrendingUp, MousePointer, Download, ArrowUpRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WidgetsPage = ({ embedded = false }) => {
  const { user } = useAuth();
  const token = localStorage.getItem('ihostpms_token');
  
  const [widgets, setWidgets] = useState([]);
  const [propertyWidgets, setPropertyWidgets] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMainTab, setActiveMainTab] = useState('property');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [widgetAnalytics, setWidgetAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    widget_name: '',
    widget_type: 'single',
    property_ids: [],
    theme: 'light',
    primary_color: '#FF385C',
    show_search: true,
    show_prices: true,
    max_properties: 10,
    compact_mode: false
  });
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [widgetsResp, propsResp] = await Promise.all([
          wordpressWidgetAPI.list().catch(() => ({ data: { widgets: [] } })),
          propertiesAPI.list()
        ]);
        setWidgets(widgetsResp.data.widgets || []);
        const props = propsResp.data.properties || propsResp.data || [];
        setProperties(props);
        
        // Load property-level widgets
        const propWidgets = [];
        for (const prop of props.slice(0, 10)) {
          try {
            const widgetResp = await widgetAPI.getByProperty(prop.property_id);
            if (widgetResp.data && widgetResp.data.widget_id) {
              propWidgets.push({
                ...widgetResp.data,
                property_name: prop.name
              });
            }
          } catch (e) {
            // Property might not have a widget yet
          }
        }
        setPropertyWidgets(propWidgets);
        setProperties(props);
      } catch (err) {
        console.error('Failed to load data:', err);
        // Don't redirect on error - let the user stay on page
      } finally {
        setLoading(false);
      }
    };
    
    // Only load if we have a token
    const token = localStorage.getItem('ihostpms_token');
    if (token) {
      loadData();
    } else {
      setLoading(false);
    }
  }, []);
  
  // Load analytics for selected widget
  const loadWidgetAnalytics = async (widgetId) => {
    setAnalyticsLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/wordpress-widget/${widgetId}/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setWidgetAnalytics(await resp.json());
      }
    } catch (err) { console.error('Analytics error:', err); }
    finally { setAnalyticsLoading(false); }
  };
  
  // Load overview analytics
  const loadAnalyticsOverview = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/widget-analytics/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) setAnalyticsOverview(await resp.json());
    } catch {}
  };

  // Load analytics when widget selected
  useEffect(() => {
    if (selectedWidget?.widget_id) loadWidgetAnalytics(selectedWidget.widget_id);
  }, [selectedWidget?.widget_id]);
  
  // Load overview on mount
  useEffect(() => {
    if (token) loadAnalyticsOverview();
  }, [token, widgets.length]);

  const createWidget = async () => {
    setCreating(true);
    try {
      const resp = await wordpressWidgetAPI.create(form);
      setWidgets([resp.data, ...widgets]);
      setSelectedWidget(resp.data);
      setShowCreateDialog(false);
      setForm({
        widget_name: '',
        widget_type: 'single',
        property_ids: [],
        theme: 'light',
        primary_color: '#FF385C',
        show_search: true,
        show_prices: true,
        max_properties: 10,
        compact_mode: false
      });
    } catch (err) {
      console.error('Failed to create widget:', err);
      alert('Failed to create widget');
    } finally {
      setCreating(false);
    }
  };
  
  const deleteWidget = async (widgetId) => {
    if (!window.confirm('Delete this widget? Embedded widgets will stop working.')) return;
    
    try {
      await wordpressWidgetAPI.delete(widgetId);
      setWidgets(widgets.filter(w => w.widget_id !== widgetId));
      if (selectedWidget?.widget_id === widgetId) {
        setSelectedWidget(null);
      }
    } catch (err) {
      console.error('Failed to delete widget:', err);
    }
  };
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  
  const getWidgetTypeInfo = (type) => {
    const info = {
      single: { label: 'Single Property', icon: Calendar, desc: 'Embed one property booking form' },
      multi: { label: 'Multi Property', icon: LayoutGrid, desc: 'Show multiple properties' },
      search: { label: 'Search Widget', icon: Search, desc: 'Full property search' }
    };
    return info[type] || info.single;
  };
  
  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-screen'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-airbnb-pink" />
      </div>
    );
  }
  
  return (
    <div className={embedded ? '' : 'p-6 max-w-7xl mx-auto'} data-testid="widgets-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Code className="w-6 h-6 text-indigo-600" />
              Booking Widgets
            </h1>
            <p className="text-gray-500 mt-1">Embed booking widgets on your website to accept direct bookings</p>
          </div>
        )}
        {embedded && (
          <div>
            <p className="text-airbnb-medium-gray">Create and manage booking widgets to embed on your website</p>
          </div>
        )}
        
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Widget
        </Button>
      </div>
      
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-indigo-900">Get More Direct Bookings</h4>
            <p className="text-sm text-indigo-700 mt-1">
              Embed booking widgets on your website to accept direct bookings without OTA fees. 
              Choose from single property widgets, multi-property displays, or full search widgets.
            </p>
          </div>
        </div>
      </div>
      
      {/* Main Tabs */}
      <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="property">
            <Building2 className="w-4 h-4 mr-2" />
            Property Widgets
          </TabsTrigger>
          <TabsTrigger value="multi">
            <LayoutGrid className="w-4 h-4 mr-2" />
            Custom Widgets
          </TabsTrigger>
        </TabsList>
        
        {/* Property Widgets Tab */}
        <TabsContent value="property" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map(prop => {
              const propWidget = propertyWidgets.find(w => w.property_id === prop.property_id);
              return (
                <Card key={prop.property_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{prop.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {prop.location?.city || 'No location'}
                        </CardDescription>
                      </div>
                      {propWidget && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          Active
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {propWidget ? (
                      <div className="space-y-3">
                        <div className="text-xs text-gray-500">
                          {propWidget.analytics?.views || 0} views • {propWidget.analytics?.bookings || 0} bookings
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => {
                              const embedCode = `<iframe src="${BACKEND_URL}/embed/${propWidget.widget_key}" width="100%" height="600" frameborder="0"></iframe>`;
                              navigator.clipboard.writeText(embedCode);
                              setCopiedCode(propWidget.widget_id);
                              setTimeout(() => setCopiedCode(null), 2000);
                            }}
                          >
                            {copiedCode === propWidget.widget_id ? (
                              <CheckCircle2 className="w-3 h-3 mr-1 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3 mr-1" />
                            )}
                            Copy Code
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(`/property/${prop.property_id}#widget`, '_blank')}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500 mb-3">No widget configured</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.location.href = `/property/${prop.property_id}#widget`}
                        >
                          Configure Widget
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            
            {properties.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No properties yet</p>
                <p className="text-sm text-gray-400">Create a property first to configure widgets</p>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Multi-Property Tab */}
        <TabsContent value="multi" className="mt-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Widget List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Custom Widgets</CardTitle>
              <CardDescription>{widgets.length} widget(s) created</CardDescription>
            </CardHeader>
            <CardContent>
              {widgets.length === 0 ? (
                <div className="text-center py-8">
                  <LayoutGrid className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">No custom widgets</p>
                  <p className="text-sm text-gray-400 mt-1">Create a widget to embed on your website</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {widgets.map(widget => {
                    const typeInfo = getWidgetTypeInfo(widget.wordpress_type);
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <div 
                        key={widget.widget_id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedWidget?.widget_id === widget.widget_id 
                            ? 'border-airbnb-pink bg-pink-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedWidget(widget)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-2">
                            <TypeIcon className="w-4 h-4 mt-0.5 text-gray-500" />
                            <div>
                              <p className="font-medium">{widget.name}</p>
                              <p className="text-xs text-gray-500">{typeInfo.label}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {widget.analytics?.views || 0} views
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Widget Details & Embed Codes */}
        <div className="lg:col-span-2">
          {selectedWidget ? (
            <Card>
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle>{selectedWidget.name}</CardTitle>
                  <CardDescription>
                    {getWidgetTypeInfo(selectedWidget.wordpress_type).desc}
                  </CardDescription>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => deleteWidget(selectedWidget.widget_id)}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="iframe">
                  <TabsList className="mb-4">
                    <TabsTrigger value="iframe">
                      <Code className="w-4 h-4 mr-2" />
                      HTML Embed
                    </TabsTrigger>
                    <TabsTrigger value="javascript">
                      <Code className="w-4 h-4 mr-2" />
                      JavaScript
                    </TabsTrigger>
                    <TabsTrigger value="wordpress">
                      <Globe className="w-4 h-4 mr-2" />
                      WordPress
                    </TabsTrigger>
                    <TabsTrigger value="preview">
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </TabsTrigger>
                    <TabsTrigger value="analytics">
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Analytics
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="iframe">
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">Iframe Embed Code</Label>
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                            {selectedWidget.embed_codes?.iframe || `<iframe src="${BACKEND_URL}/widget/wp/${selectedWidget.widget_key}" width="100%" height="600" frameborder="0"></iframe>`}
                          </pre>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(selectedWidget.embed_codes?.iframe || '', 'iframe')}
                          >
                            {copiedCode === 'iframe' ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-700">
                          <strong>Instructions:</strong> Copy this code and paste it into an HTML block in your WordPress page or post editor.
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="javascript">
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">JavaScript Embed Code</Label>
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
                            {selectedWidget.embed_codes?.javascript || `<div id="ihostpms-widget-${selectedWidget.widget_key}"></div>\n<script src="${BACKEND_URL}/widget-loader.js" data-widget-key="${selectedWidget.widget_key}"></script>`}
                          </pre>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(selectedWidget.embed_codes?.javascript || '', 'js')}
                          >
                            {copiedCode === 'js' ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">
                          <strong>For Developers:</strong> This method gives more control over widget loading and styling.
                        </p>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="wordpress">
                    <div className="space-y-4">
                      <div>
                        <Label className="mb-2 block">WordPress Shortcode</Label>
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm">
                            {`[ihostpms_widget key="${selectedWidget.widget_key}" height="600"]`}
                          </pre>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="absolute top-2 right-2"
                            onClick={() => copyToClipboard(`[ihostpms_widget key="${selectedWidget.widget_key}" height="600"]`, 'shortcode')}
                          >
                            {copiedCode === 'shortcode' ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Plugin Download */}
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-purple-800">iHostPMS WordPress Plugin</p>
                            <p className="text-xs text-purple-600 mt-0.5">Includes shortcodes, Gutenberg block, and admin settings</p>
                          </div>
                          <a href={`${BACKEND_URL}/api/wordpress-plugin/download`}
                            onClick={(e) => {
                              e.preventDefault();
                              fetch(`${BACKEND_URL}/api/wordpress-plugin/download`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              }).then(r => r.blob()).then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url; a.download = 'ihostpms-widget.zip'; a.click();
                                URL.revokeObjectURL(url);
                              });
                            }}>
                            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="download-wp-plugin">
                              <Download className="w-4 h-4 mr-1" />
                              Download Plugin
                            </Button>
                          </a>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-700 font-medium mb-2">Installation Steps:</p>
                        <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
                          <li>Download the plugin ZIP file above</li>
                          <li>Go to WordPress Admin &gt; Plugins &gt; Add New &gt; Upload Plugin</li>
                          <li>Upload the ZIP file and activate the plugin</li>
                          <li>Go to Settings &gt; iHostPMS to configure your API URL</li>
                          <li>Use the shortcode above or the Gutenberg block in your pages</li>
                        </ol>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="preview">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Widget Preview</Label>
                        <Button variant="outline" size="sm" asChild>
                          <a 
                            href={`${BACKEND_URL}/widget/wp/${selectedWidget.widget_key}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Open in New Tab
                          </a>
                        </Button>
                      </div>
                      
                      <div className="border rounded-lg overflow-hidden bg-gray-50" style={{ height: '500px' }}>
                        <iframe
                          src={`${BACKEND_URL}/widget/wp/${selectedWidget.widget_key}`}
                          title="Widget Preview"
                          className="w-full h-full border-0"
                          data-testid="widget-preview-iframe"
                        />
                      </div>
                      <p className="text-xs text-gray-400 text-center">
                        This is a live preview of how the widget will appear when embedded on your website.
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="analytics">
                    <div className="space-y-4" data-testid="widget-analytics-tab">
                      {analyticsLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-airbnb-pink" />
                        </div>
                      ) : widgetAnalytics ? (
                        <>
                          {/* Summary Cards */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <div className="bg-blue-50 rounded-xl p-3 text-center">
                              <Eye className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                              <p className="text-xl font-bold text-blue-800">{widgetAnalytics.summary.total_views}</p>
                              <p className="text-xs text-blue-500">Total Views</p>
                            </div>
                            <div className="bg-amber-50 rounded-xl p-3 text-center">
                              <MousePointer className="w-5 h-5 mx-auto mb-1 text-amber-500" />
                              <p className="text-xl font-bold text-amber-800">{widgetAnalytics.summary.total_clicks}</p>
                              <p className="text-xs text-amber-500">Clicks</p>
                            </div>
                            <div className="bg-green-50 rounded-xl p-3 text-center">
                              <Calendar className="w-5 h-5 mx-auto mb-1 text-green-500" />
                              <p className="text-xl font-bold text-green-800">{widgetAnalytics.summary.total_bookings}</p>
                              <p className="text-xs text-green-500">Bookings</p>
                            </div>
                          </div>
                          
                          {/* Conversion Rates */}
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <p className="text-lg font-bold text-indigo-600">{widgetAnalytics.summary.click_rate}%</p>
                              <p className="text-[10px] text-gray-500">Click Rate</p>
                            </div>
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <p className="text-lg font-bold text-emerald-600">{widgetAnalytics.summary.booking_rate}%</p>
                              <p className="text-[10px] text-gray-500">Booking Rate</p>
                            </div>
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <p className="text-lg font-bold text-rose-600">{widgetAnalytics.summary.conversion_rate}%</p>
                              <p className="text-[10px] text-gray-500">Conversion</p>
                            </div>
                          </div>
                          
                          {/* 30-Day Trend Chart */}
                          {widgetAnalytics.daily_trend && (
                            <div className="bg-white border rounded-xl p-4">
                              <h4 className="text-sm font-semibold text-gray-700 mb-3">Last 30 Days</h4>
                              <div className="flex items-end gap-px h-32" data-testid="analytics-chart">
                                {widgetAnalytics.daily_trend.map((d, i) => {
                                  const maxVal = Math.max(...widgetAnalytics.daily_trend.map(x => x.views + x.clicks + x.bookings), 1);
                                  const barH = Math.max(((d.views + d.clicks + d.bookings) / maxVal) * 100, 2);
                                  return (
                                    <div key={i} className="flex-1 flex flex-col items-center group relative">
                                      <div className="absolute -top-8 bg-gray-800 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                                        {d.date}: {d.views}v {d.clicks}c {d.bookings}b
                                      </div>
                                      <div 
                                        className="w-full rounded-t bg-gradient-to-t from-rose-400 to-rose-300 transition-all hover:from-rose-500 hover:to-rose-400"
                                        style={{ height: `${barH}%`, minHeight: '2px' }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-[9px] text-gray-400">{widgetAnalytics.daily_trend[0]?.date}</span>
                                <span className="text-[9px] text-gray-400">{widgetAnalytics.daily_trend[widgetAnalytics.daily_trend.length - 1]?.date}</span>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8">
                          <BarChart3 className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                          <p className="text-sm text-gray-500">No analytics data yet</p>
                          <p className="text-xs text-gray-400">Share your widget to start collecting data</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
                
                {/* Widget Stats Quick View */}
                <div className="mt-6 pt-6 border-t grid grid-cols-4 gap-3" data-testid="widget-stats-quick">
                  <div className="text-center">
                    <p className="text-xl font-bold text-blue-600">{selectedWidget.analytics?.views || 0}</p>
                    <p className="text-xs text-gray-500">Views</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-amber-600">{(selectedWidget.analytics?.clicks || 0) + (selectedWidget.analytics?.book_now_clicks || 0)}</p>
                    <p className="text-xs text-gray-500">Clicks</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">{selectedWidget.analytics?.bookings || 0}</p>
                    <p className="text-xs text-gray-500">Bookings</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-rose-600">
                      {selectedWidget.analytics?.views > 0 
                        ? ((selectedWidget.analytics?.bookings / selectedWidget.analytics?.views) * 100).toFixed(1)
                        : 0}%
                    </p>
                    <p className="text-xs text-gray-500">Conversion</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-20">
                <div className="text-center">
                  <Code className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">Select a widget to view embed codes</p>
                  <p className="text-sm text-gray-400 mt-1">Or create a new widget to get started</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>
      
      {/* Analytics Overview */}
      {analyticsOverview && analyticsOverview.overview.total_widgets > 0 && (
        <Card className="mt-6" data-testid="widget-analytics-overview">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-airbnb-pink" />
              Widget Performance Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold">{analyticsOverview.overview.total_widgets}</p>
                <p className="text-[10px] text-gray-500">Widgets</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-blue-700">{analyticsOverview.overview.total_views}</p>
                <p className="text-[10px] text-blue-500">Total Views</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-amber-700">{analyticsOverview.overview.total_clicks}</p>
                <p className="text-[10px] text-amber-500">Total Clicks</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-green-700">{analyticsOverview.overview.total_bookings}</p>
                <p className="text-[10px] text-green-500">Bookings</p>
              </div>
              <div className="bg-rose-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-rose-700">{analyticsOverview.overview.overall_conversion_rate}%</p>
                <p className="text-[10px] text-rose-500">Conversion</p>
              </div>
            </div>
            {analyticsOverview.widgets.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="widget-analytics-table">
                  <thead>
                    <tr className="border-b text-xs text-gray-500">
                      <th className="text-left py-2 px-2">Widget</th>
                      <th className="text-center py-2 px-2">Type</th>
                      <th className="text-center py-2 px-2">Views</th>
                      <th className="text-center py-2 px-2">Clicks</th>
                      <th className="text-center py-2 px-2">Bookings</th>
                      <th className="text-center py-2 px-2">Click Rate</th>
                      <th className="text-center py-2 px-2">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsOverview.widgets.map(w => (
                      <tr key={w.widget_id} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2 px-2 font-medium">{w.name}</td>
                        <td className="py-2 px-2 text-center"><Badge variant="outline" className="text-[10px]">{w.type}</Badge></td>
                        <td className="py-2 px-2 text-center">{w.views}</td>
                        <td className="py-2 px-2 text-center">{w.clicks}</td>
                        <td className="py-2 px-2 text-center font-medium text-green-600">{w.bookings}</td>
                        <td className="py-2 px-2 text-center">{w.click_rate}%</td>
                        <td className="py-2 px-2 text-center font-medium text-rose-600">{w.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Create Widget Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Booking Widget</DialogTitle>
            <DialogDescription>
              Create an embeddable widget for your website
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Widget Name</Label>
              <Input
                value={form.widget_name}
                onChange={(e) => setForm({...form, widget_name: e.target.value})}
                placeholder="e.g., Homepage Booking Widget"
              />
            </div>
            
            <div>
              <Label>Widget Type</Label>
              <Select
                value={form.widget_type}
                onValueChange={(val) => setForm({...form, widget_type: val})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Single Property</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="multi">
                    <div className="flex items-center gap-2">
                      <LayoutGrid className="w-4 h-4" />
                      <span>Multiple Properties</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="search">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      <span>Property Search</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {form.widget_type === 'single' && 'Embed a booking form for one property'}
                {form.widget_type === 'multi' && 'Show multiple properties with booking options'}
                {form.widget_type === 'search' && 'Full search widget like a mini-marketplace'}
              </p>
            </div>
            
            {(form.widget_type === 'single' || form.widget_type === 'multi') && (
              <div>
                <Label>Select Properties</Label>
                <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto mt-1">
                  {properties.map(prop => (
                    <label key={prop.property_id} className="flex items-center gap-2 py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.property_ids.includes(prop.property_id)}
                        onChange={(e) => {
                          if (form.widget_type === 'single') {
                            setForm({...form, property_ids: e.target.checked ? [prop.property_id] : []});
                          } else {
                            setForm({
                              ...form,
                              property_ids: e.target.checked
                                ? [...form.property_ids, prop.property_id]
                                : form.property_ids.filter(id => id !== prop.property_id)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{prop.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Theme</Label>
                <Select
                  value={form.theme}
                  onValueChange={(val) => setForm({...form, theme: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({...form, primary_color: e.target.value})}
                    className="w-12 h-10 p-1"
                  />
                  <Input
                    value={form.primary_color}
                    onChange={(e) => setForm({...form, primary_color: e.target.value})}
                    placeholder="#FF385C"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Prices</Label>
                <p className="text-xs text-gray-500">Display property prices in widget</p>
              </div>
              <Switch
                checked={form.show_prices}
                onCheckedChange={(val) => setForm({...form, show_prices: val})}
              />
            </div>
            
            {form.widget_type === 'search' && (
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Search Bar</Label>
                  <p className="text-xs text-gray-500">Allow guests to search properties</p>
                </div>
                <Switch
                  checked={form.show_search}
                  onCheckedChange={(val) => setForm({...form, show_search: val})}
                />
              </div>
            )}
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Compact Mode</Label>
                <p className="text-xs text-gray-500">Smaller widget for sidebars</p>
              </div>
              <Switch
                checked={form.compact_mode}
                onCheckedChange={(val) => setForm({...form, compact_mode: val})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createWidget} disabled={creating || !form.widget_name}>
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Widget'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WidgetsPage;
